import { useState } from 'react';
import api from '../services/api';
import { errorMessage, openProtectedFile } from '../lib/http';
import { BTN_SOFT, BTN_SOFT_STYLE, FIELD, FIELD_STYLE } from '../lib/ui';
import { IconExternal } from './NavIcons';
import { Spinner } from './ui';
import type { CompanyDocument } from '../types/workspace';

/** Re-verify / approve / reject controls for one company document. */
export default function AdminDocActions({
  companyId,
  doc,
  onChanged,
  onError,
  hideView = false,
}: {
  companyId: string;
  doc: CompanyDocument;
  onChanged: () => void;
  onError: (m: string) => void;
  /** When true, omit the standalone View button (in-page viewer is used instead). */
  hideView?: boolean;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState(doc.reviewNote || '');
  const base = `/admin/companies/${companyId}/documents/${doc.id}`;
  const verifying = doc.status === 'VERIFYING' || doc.status === 'UPLOADED';

  const run = async (kind: string, fn: () => Promise<unknown>) => {
    setBusy(kind);
    try {
      await fn();
      setRejecting(false);
      onChanged();
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {!hideView && (
          <button type="button" onClick={() => void openProtectedFile(`${base}/file`).catch(() => onError('Could not open file.'))} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
            <IconExternal className="h-3.5 w-3.5" /> View
          </button>
        )}
        {!verifying && (
          <button type="button" disabled={busy !== null} onClick={() => void run('verify', () => api.post(`${base}/verify`))} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
            {busy === 'verify' && <Spinner className="h-3 w-3" />} Re-run AI
          </button>
        )}
        {doc.status !== 'VERIFIED' && !verifying && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void run('approve', () => api.patch(base, { status: 'VERIFIED', reviewNote: note || null }))}
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold text-[#047857] hover:bg-emerald-100"
            style={{ background: '#ecfdf5', border: '1px solid #a7f3d0' }}
          >
            {busy === 'approve' && <Spinner className="h-3 w-3" />} Approve
          </button>
        )}
        {doc.status !== 'REJECTED' && !verifying && (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => setRejecting((v) => !v)}
            className="rounded-xl px-3 py-2 text-xs font-bold text-[#be123c] hover:bg-rose-100"
            style={{ background: '#fff1f2', border: '1px solid #fecdd3' }}
          >
            Reject…
          </button>
        )}
      </div>
      {rejecting && (
        <div className="flex flex-wrap gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tell the company what to fix" className={`${FIELD} min-w-[220px] flex-1 py-2`} style={FIELD_STYLE} />
          <button
            type="button"
            disabled={busy !== null || !note.trim()}
            onClick={() => void run('reject', () => api.patch(base, { status: 'REJECTED', reviewNote: note.trim() }))}
            className="rounded-xl bg-[#be123c] px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
          >
            {busy === 'reject' ? 'Saving…' : 'Confirm reject'}
          </button>
        </div>
      )}
    </div>
  );
}
